import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  ChevronRight, 
  Plane, 
  Code, 
  Shield, 
  Zap, 
  Heart,
  Target,
} from "lucide-react";

const AboutPage = () => {
  const values = [
    {
      icon: Code,
      title: "Quality Code",
      description: "Every script is developed with clean, readable code following best software practices."
    },
    {
      icon: Zap,
      title: "Optimization",
      description: "Server performance is always our priority. All scripts are optimized for low resource usage."
    },
    {
      icon: Target,
      title: "Realism",
      description: "Our passion for aviation inspires us to bring real-world systems to FiveM."
    },
    {
      icon: Heart,
      title: "Community Focused",
      description: "Customer feedback is valuable to us. We continuously improve and update."
    }
  ];

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-16 relative">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="hero-glow opacity-30" />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-xs uppercase tracking-widest text-primary mb-4 block">
              About Us
            </span>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight uppercase mb-4">
              AeroCore Scripts
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Focused development for aviation roleplay communities
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="group relative aspect-square overflow-hidden rounded-2xl border border-primary/20 bg-slate-900/50 shadow-[0_0_45px_rgba(0,183,255,0.08)]">
                <img
                  src="/images/about/founder-portrait.webp"
                  alt="Founder of AeroCore Scripts"
                  width="1200"
                  height="1200"
                  loading="lazy"
                  className="h-full w-full object-cover object-center transition-transform duration-700 group-hover:scale-[1.02]"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-950/35 via-transparent to-primary/5" />
              </div>
              {/* Decorative element */}
              <div className="absolute -bottom-6 -right-6 w-32 h-32 border border-primary/30 rounded-xl hidden lg:block" />
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-primary/5 rounded-xl hidden lg:block" />
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-xs uppercase tracking-widest text-primary mb-4 block">
                Our Story
              </span>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight uppercase mb-6">
                Started with a Passion for Aviation
              </h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  AeroCore Scripts was born at the intersection of aviation and gaming passion. 
                  We saw the need for realistic and quality aviation systems in the FiveM community 
                  and set out to fill that gap.
                </p>
                <p>
                  Each system is designed around clear in-game workflows, dependable performance,
                  and the practical needs of server owners and players.
                </p>
                <p>
                  Our goal is to provide unique and immersive aviation experiences to FiveM servers. 
                  Quality, optimization, and customer satisfaction are always our priority.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-xs uppercase tracking-widest text-primary mb-4 block">
              Our Values
            </span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight uppercase mb-4">
              What Makes Us Different?
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-slate-950/50 border border-slate-800 rounded-xl p-6 hover:border-primary/30 transition-colors group"
              >
                <div className="w-12 h-12 bg-primary/10 border border-primary/30 rounded-lg flex items-center justify-center mb-4 group-hover:border-primary/60 transition-colors">
                  <value.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold tracking-wide mb-2">{value.title}</h3>
                <p className="text-sm text-muted-foreground">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 relative bg-gradient-to-b from-transparent to-primary/5">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Plane className="w-12 h-12 text-primary mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight uppercase mb-6">
              Let's Work Together
            </h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Looking for custom aviation solutions for your FiveM server? 
              We'd be happy to work with you.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/products">
                <Button
                  data-testid="about-products-btn"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg border-l-4 border-accent uppercase tracking-widest font-bold px-8 py-6 glow-blue-hover"
                >
                  Browse Products
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  data-testid="about-contact-btn"
                  variant="outline"
                  className="bg-transparent border border-primary/30 text-primary hover:bg-primary/10 rounded-lg uppercase tracking-widest px-8 py-6"
                >
                  Contact Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
